import * as React from "react";

type ToastProps = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

type ToastInput = Omit<ToastProps, "id">;

const listeners = new Set<(toasts: ToastProps[]) => void>();
let memoryToasts: ToastProps[] = [];

function emit() {
  listeners.forEach(listener => listener(memoryToasts));
}

function dismiss(id?: string) {
  memoryToasts = id ? memoryToasts.filter(toast => toast.id !== id) : [];
  emit();
}

export function toast(input: ToastInput) {
  const id = crypto.randomUUID();
  const nextToast: ToastProps = {
    ...input,
    id,
    open: true,
    onOpenChange: open => {
      if (!open) {
        dismiss(id);
      }
    },
  };

  memoryToasts = [nextToast, ...memoryToasts].slice(0, 5);
  emit();

  return {
    id,
    dismiss: () => dismiss(id),
  };
}

export function useToast() {
  const [toasts, setToasts] = React.useState<ToastProps[]>(memoryToasts);

  React.useEffect(() => {
    listeners.add(setToasts);
    return () => {
      listeners.delete(setToasts);
    };
  }, []);

  return {
    toasts,
    toast,
    dismiss,
  };
}
